if (!customElements.get('jl-equipment-gallery')) {
  customElements.define('jl-equipment-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.frame = this.querySelector('[data-gallery-frame]');
      this.slides = [...this.querySelectorAll('[data-equipment-slide]')];
      this.dots = [...this.querySelectorAll('[data-gallery-index]')];
      this.counter = this.querySelector('[data-gallery-counter]');
      this.status = this.querySelector('[data-gallery-status]');
      if (!this.frame || !this.slides.length) return;
      this.current = 0;
      this.version = (this.version || 0) + 1;
      this.failed = new Set();
      this.hovered = false;
      this.pressed = false;
      this.inView = false;
      this.interval = Math.max(2000, Number(this.dataset.interval) || 3000);
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.manual(this.nextIndex(-1)), options);
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.manual(this.nextIndex(1)), options);
      this.dots.forEach(dot => dot.addEventListener('click', () => this.manual(Number(dot.dataset.galleryIndex)), options));
      this.frame.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        event.stopPropagation();
        this.manual(this.nextIndex(event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') { this.hovered = true; clearTimeout(this.timer); }
      }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(3000); }, options);
      this.frame.addEventListener('pointerdown', event => {
        this.pressed = true;
        clearTimeout(this.timer);
        this.start = this.closest('jl-horizontal-carousel') || event.target.closest('button') ? null : { id: event.pointerId, x: event.clientX, y: event.clientY };
      }, options);
      window.addEventListener('pointerup', event => {
        this.pressed = false;
        const start = this.start;
        this.start = null;
        if (start && start.id === event.pointerId) {
          const dx = event.clientX - start.x;
          const dy = event.clientY - start.y;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            this.suppressClickUntil = performance.now() + 400;
            this.manual(this.nextIndex(dx < 0 ? 1 : -1));
            return;
          }
        }
        this.schedule(3000);
      }, options);
      window.addEventListener('pointercancel', () => { this.start = null; this.pressed = false; this.schedule(3000); }, options);
      this.frame.addEventListener('click', event => {
        if (performance.now() < (this.suppressClickUntil || 0) && event.target.closest('a[data-equipment-slide]')) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { ...options, capture: true });
      this.addEventListener('focusin', () => clearTimeout(this.timer), options);
      this.addEventListener('focusout', () => queueMicrotask(() => this.schedule(3000)), options);
      document.addEventListener('visibilitychange', () => this.schedule(3000), options);
      this.motion.addEventListener('change', () => this.schedule(3000), options);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        if (this.inView && this.slides.length > 1) this.prepare(this.nextIndex(1));
        this.schedule(3000);
      }, { threshold: 0.1 });
      this.observer.observe(this);
    }

    nextIndex(direction) {
      for (let step = 1; step < this.slides.length; step++) {
        const index = (this.current + direction * step + this.slides.length) % this.slides.length;
        if (!this.failed.has(index)) return index;
      }
      return this.current;
    }

    hasKeyboardFocus() {
      const active = document.activeElement;
      return !!active && this.contains(active) && active.matches(':focus-visible');
    }

    canPlay() {
      return this.isConnected && !!this.controller && this.dataset.autoplay === 'true' && this.slides.length > 1 && this.inView && !document.hidden &&
        !this.hovered && !this.pressed && !this.hasKeyboardFocus() && !this.motion.matches;
    }

    async prepare(index) {
      const image = this.slides[index]?.querySelector('img');
      if (!image) return false;
      image.loading = 'eager';
      try {
        if (!image.complete || !image.naturalWidth) {
          if (image.decode) await image.decode();
          else await new Promise((resolve, reject) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', reject, { once: true });
          });
        }
        return image.naturalWidth > 0;
      } catch { return false; }
    }

    async show(index, userInitiated = false) {
      if (!Number.isInteger(index) || index < 0 || index >= this.slides.length || index === this.current) return;
      const version = ++this.version;
      const loaded = await this.prepare(index);
      if (!this.isConnected || version !== this.version) return;
      if (!loaded) {
        this.failed.add(index);
        if (this.dots[index]) this.dots[index].disabled = true;
        if (userInitiated && this.status) this.status.textContent = document.documentElement.lang.startsWith('zh') ? '图片暂时无法加载，请稍后重试。' : 'Photo unavailable. Please try again later.';
        return;
      }
      if (!userInitiated && !this.canPlay()) return;
      this.slides.forEach((slide, i) => { slide.hidden = i !== index; });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === index)));
      this.current = index;
      const label = (index + 1) + ' / ' + this.slides.length;
      if (this.counter) this.counter.textContent = label;
      if (userInitiated && this.status) this.status.textContent = document.documentElement.lang.startsWith('zh') ? '第 ' + (index + 1) + ' 张，共 ' + this.slides.length + ' 张' : 'Photo ' + (index + 1) + ' of ' + this.slides.length;
      this.prepare(this.nextIndex(1));
    }

    async manual(index) {
      clearTimeout(this.timer);
      await this.show(index, true);
      this.schedule(3000);
    }

    schedule(delay = this.interval) {
      clearTimeout(this.timer);
      if (!this.canPlay()) return;
      this.timer = setTimeout(async () => {
        await this.show(this.nextIndex(1));
        this.schedule();
      }, delay);
    }

    disconnectedCallback() {
      this.version++;
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      clearTimeout(this.timer);
    }
  });
}
